import { useState, useCallback } from 'react';
import { logger } from '../services/logger/logger';
import { showSuccessToast } from '../components/common/BaseToaster';

export interface UseDeleteConfirmationProps {
  deleteMutation: (_id: any) => { unwrap: () => Promise<any> };
  refetch: () => void;
  itemName?: string;
  idField?: string;
}

export const useDeleteConfirmation = <T extends Record<string, any>>({
  deleteMutation,
  refetch,
  itemName = 'item',
  idField = 'id',
}: UseDeleteConfirmationProps) => {
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const id = deleteTarget[idField];
      const response: any = await deleteMutation(id).unwrap();
      showSuccessToast(response?.message);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      refetch();
    } catch (e: any) {
      logger.error(`Failed to delete ${itemName}`, e as Error);
    }
  }, [deleteTarget, deleteMutation, refetch, itemName, idField]);

  const onDeleteClick = useCallback((item: T) => {
    setDeleteTarget(item);
    setShowDeleteConfirm(true);
  }, []);

  const onCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  }, []);

  return {
    deleteTarget,
    setDeleteTarget,
    showDeleteConfirm,
    setShowDeleteConfirm,
    handleConfirmDelete,
    onDeleteClick,
    onCloseDeleteConfirm,
  };
};
